import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Truck } from "lucide-react";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import { formatNumber, formatVolumeUnit } from "@/lib/format";
import { hasPalletData } from "@/features/reports/components/fleetLoad";
import { useVolumeUnit } from "@/hooks/useVolumeUnit";
import type { FleetLoadRow } from "@/types/reports";

interface Props {
  rows: FleetLoadRow[];
  isLoading: boolean;
}

const SKELETON_ROWS = 5;
const DASH = "—";

/** Renders an em-dash placeholder cell value. */
function dash() {
  return <span className="text-gray-300">{DASH}</span>;
}

/**
 * Formats a finite number to at most one decimal with locale-aware thousands
 * grouping (TASK-3840); guards against NaN/Infinity. Percentages pass through
 * the same formatter so e.g. `1234.5 → "1 234,5"`.
 */
function formatAmount(value: number): string {
  if (!Number.isFinite(value)) return DASH;
  return formatNumber(Math.round(value * 10) / 10, undefined, DASH);
}

/** Colour ramp for a utilization percentage; null is muted. */
function pctClass(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return "text-gray-400";
  if (pct >= 90) return "text-rose-600 font-semibold";
  if (pct >= 70) return "text-amber-600 font-semibold";
  return "text-emerald-600 font-semibold";
}

/**
 * Renders a "used / capacity" pair plus a coloured utilization percentage.
 * Capacity null/zero → em-dash for both the capacity and the percentage
 * (Sprint 107 null-guard); the percentage is always taken from the backend
 * (already null-safe) rather than recomputed on the client.
 */
function loadCell(used: number, capacity: number | null, pct: number | null) {
  const hasCapacity = capacity !== null && Number.isFinite(capacity);
  return (
    <div className="flex flex-col items-end gap-0.5 tabular-nums">
      <span className="text-gray-700">
        {formatAmount(used)}
        <span className="text-gray-400"> / </span>
        {hasCapacity ? formatAmount(capacity as number) : DASH}
      </span>
      <span className={cn("text-xs", pctClass(pct))}>
        {pct !== null && Number.isFinite(pct) ? `${formatAmount(pct)}%` : DASH}
      </span>
    </div>
  );
}

/**
 * Per-vehicle Fleet Load table (TASK-3830). Client-side text filter on the
 * vehicle number. Columns: Vehicle, Trips, Weight, Volume, Pallets — each load
 * column shows used/capacity plus the backend-supplied utilization percentage
 * (em-dash when capacity is 0/null).
 */
export function FleetLoadTable({ rows, isLoading }: Props) {
  const { t } = useTranslation();
  const volumeUnit = useVolumeUnit();
  const [filter, setFilter] = useState("");

  const filteredRows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      (row.vehicle_number ?? "").toLowerCase().includes(needle),
    );
  }, [rows, filter]);

  // Hide the all-empty Pallets column when the tenant doesn't track pallets
  // (every vehicle is 0/—). Computed over the full result set, not the filtered
  // view, so a text filter never makes the column flicker (TASK-3848).
  const showPallets = useMemo(() => hasPalletData(rows), [rows]);
  const columnCount = showPallets ? 5 : 4;

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-3">
        <input
          type="text"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder={t("analytics.fleet.filterVehicles")}
          aria-label={t("analytics.fleet.filterVehicles")}
          className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-100"
        />
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/60 text-left text-xs font-semibold uppercase tracking-widest text-gray-400">
            <th className="px-5 py-3.5">{t("analytics.fleet.vehicle")}</th>
            <th className="px-5 py-3.5 text-right">
              {t("analytics.fleet.trips")}
            </th>
            <th className="px-5 py-3.5 text-right">
              {t("analytics.fleet.weightKg")}
            </th>
            <th className="px-5 py-3.5 text-right">
              {t("analytics.fleet.volumeM3", {
                unit: formatVolumeUnit(volumeUnit),
              })}
            </th>
            {showPallets && (
              <th className="px-5 py-3.5 text-right">
                {t("analytics.fleet.pallets")}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <tr key={i} className="border-b border-gray-50 last:border-0">
                  {Array.from({ length: columnCount }).map((_, j) => (
                    <td key={j} className="px-5 py-4">
                      <Skeleton className="h-4 w-20" />
                    </td>
                  ))}
                </tr>
              ))
            : filteredRows.map((row) => (
                <tr
                  key={row.vehicle_id}
                  className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                >
                  <td className="px-5 py-4 font-semibold text-gray-900">
                    {row.vehicle_number ?? dash()}
                  </td>
                  <td className="px-5 py-4 text-right tabular-nums text-gray-500">
                    {formatNumber(row.trips)}
                  </td>
                  <td className="px-5 py-4">
                    {loadCell(
                      row.weight_used_kg,
                      row.weight_capacity_kg,
                      row.utilization_weight_pct,
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {loadCell(
                      row.volume_used_m3,
                      row.volume_capacity_m3,
                      row.utilization_volume_pct,
                    )}
                  </td>
                  {showPallets && (
                    <td className="px-5 py-4">
                      {loadCell(
                        row.pallets_used,
                        row.pallets_capacity,
                        row.utilization_pallets_pct,
                      )}
                    </td>
                  )}
                </tr>
              ))}
        </tbody>
      </table>

      {!isLoading && filteredRows.length === 0 && (
        <EmptyState icon={Truck} title={t("analytics.fleet.noRecords")} />
      )}
    </div>
  );
}
